import { useState } from "react";
import { BEFORE_AFTER, SHOWCASE_IMAGES } from "../lib/config";
import { X } from "lucide-react";

function ImageCard({ src, label, badgeColor, onClick }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <button type="button" onClick={onClick} className="relative block w-full h-full overflow-hidden">
      {!loaded && <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 to-emerald-100 animate-pulse" />}
      <img
        src={src}
        alt={label}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        className={`w-full h-full object-cover transition-all duration-700 ${loaded ? "opacity-100 scale-100" : "opacity-0 scale-105"} hover:scale-105`}
      />
      <div className={`absolute top-2 left-2 ${badgeColor} text-white text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-1 rounded`}>
        {label}
      </div>
    </button>
  );
}

export default function Gallery() {
  const [zoom, setZoom] = useState(null); // {src, title}

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="gallery-page">
      <div className="text-xs uppercase tracking-[0.2em] text-[#047857] font-bold">Gallery</div>
      <h1 className="mt-1 text-4xl sm:text-5xl font-extrabold tracking-tighter text-gray-900 font-[Outfit]">Before & After</h1>
      <p className="mt-3 text-gray-600 max-w-xl">Real Jacksonville yards. No filters, no edits — just before and after. Tap any photo to zoom.</p>

      {/* BEFORE/AFTER PAIRS */}
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {BEFORE_AFTER.map((b, i) => (
          <div key={i} className="rounded-2xl overflow-hidden border border-gray-200 bg-white hover:shadow-xl hover:shadow-black/5 hover:-translate-y-0.5 transition-all" data-testid={`gallery-item-${i}`}>
            <div className="grid grid-cols-2 h-52">
              <ImageCard src={b.before} label="Before" badgeColor="bg-black/70" onClick={() => setZoom({ src: b.beforeFull || b.before, title: `${b.title} — Before` })} />
              <ImageCard src={b.after} label="After" badgeColor="bg-[#047857]" onClick={() => setZoom({ src: b.afterFull || b.after, title: `${b.title} — After` })} />
            </div>
            <div className="px-4 py-3 text-sm font-semibold text-gray-900">{b.title}</div>
          </div>
        ))}
      </div>

      {/* RECENT WORK GRID */}
      <div className="mt-12">
        <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900 font-[Outfit] mb-4">Recent work</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {SHOWCASE_IMAGES.map((src, i) => (
            <button
              key={i}
              onClick={() => setZoom({ src, title: "Recent JDSN work" })}
              className="relative aspect-square rounded-2xl overflow-hidden bg-gray-100 border border-gray-200 hover:shadow-lg transition-shadow"
              data-testid={`showcase-${i}`}
            >
              <img src={src} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 hover:scale-110" />
            </button>
          ))}
        </div>
      </div>

      {/* ZOOM MODAL */}
      {zoom && (
        <div
          className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 animate-in"
          onClick={() => setZoom(null)}
          data-testid="gallery-zoom"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setZoom(null); }}
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
            aria-label="Close"
            data-testid="gallery-zoom-close"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="max-w-4xl w-full">
            <img src={zoom.src} alt={zoom.title} className="w-full h-auto max-h-[82vh] object-contain rounded-2xl" />
            <div className="mt-3 text-center text-white/85 text-sm font-medium">{zoom.title}</div>
          </div>
        </div>
      )}
    </div>
  );
}
